import React, { useState } from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Dialog, DialogProps, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import { SaveTaskDto, Task, TaskStatus } from '../../../app/entities/task';
import { StatusSelector } from '../../process/components/status-selector';
import { Project, ProjectPriority } from '../../../app/entities/project';
import { PriorityIcon, PrioritySelector } from '../../process/components/priority-selector';
import { TaskProjectSelector } from './task-project-selector';
import TaskStepSelector from './task-step-selector';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import { breakPoint } from '../../../app/theme';
import { AssigneeSelector } from '../../../components/assignee-selector/assignee-selector';
import { TagsViewer } from '../../tags/components/tags-viewer';

import { TaskPropertyButton } from './task-property.button';
import { priorities } from '../priorities';
import { CreateTaskDateProperty } from './create-task-date-property';
import { Editor } from '../../../components/editor/editor';

import styles from './create-task.modal.module.scss';

type SaveTaskModalProps = DialogProps & {
  title: string;
  defaultValues?: Partial<SaveTaskDto>;
  project?: Project;
  onCancel: () => void;
};

export const CreateTaskModal: React.FC<SaveTaskModalProps> = ({
  title,
  onCancel,
  defaultValues,
  project,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const level = project ? 'project' : 'space';

  const createSchema = yup.object().shape({
    title: yup.string().max(128, 'Max 128 characters').required(),
    status: yup.mixed<TaskStatus>().oneOf(Object.values(TaskStatus), 'Invalid status').required(),
    description: yup.string().max(10000, 'Max 10000 characters').optional(),
    priority: yup
      .mixed<ProjectPriority>()
      .oneOf(
        [
          ProjectPriority.NO_PRIORITY,
          ProjectPriority.LOW,
          ProjectPriority.MEDIUM,
          ProjectPriority.HIGH,
          ProjectPriority.URGENT,
        ],
        'Invalid priority',
      )
      .optional(),
    dueDate: yup
      .string()
      .transform((_, val) => {
        // if user clicked on the "clear" button
        if (val === '') return;
        return val;
      })
      .optional(),
    stepId: yup
      .string()
      .optional(),
    assigneeId: yup.string().optional(),
    projectId: yup
      .string()
      .optional(),
    tagsIds: yup.array().of(yup.string()).optional(),
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SaveTaskDto>({
    mode: 'onSubmit',
    resolver: yupResolver(createSchema),
    defaultValues: {
      title: '',
      description: '',
      status: TaskStatus.Todo,
      priority: ProjectPriority.NO_PRIORITY,
      dueDate: undefined,
      projectId: project?._id,
      assigneeId: undefined,
      tagsIds: [],
      ...defaultValues,
    },
  });

  const [watchedProjectId] = watch(['projectId']);

  const onSubmit = async (input: SaveTaskDto) => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    for (const key in input) {
      // @ts-ignore
      if (input[key] === '') delete input[key];
    }

    await Task.create(input);

    onCancel();
    setIsLoading(false);
  };

  return (
    <Dialog
      title={title}
      onClose={onCancel}
      actions={[
        <TextButton key={'cancel'} onClick={onCancel} text={'Cancel'} />,
        <ElevatedButton
          key="save"
          text={'Create task'}
          onClick={handleSubmit(onSubmit)}
          isLoading={isLoading}
        />,
      ]}
    >
      <Content>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            label="Task title"
            placeholder="e.g. Write a compelling pitch for an indie film project"
            required
            {...register('title')}
            errorMessage={errors.title?.message}
          />

          <Controller
            name="description"
            control={control}
            render={({ field: { onChange } }) => (
              <Editor onChange={onChange} placeholder="Add description..." />
            )}
          />

          <div className={styles.properties}>
            <Controller
              name="status"
              control={control}
              render={({ field: { onChange, value } }) => (
                <StatusSelector
                  onChange={onChange}
                  statusMap={Task.StatusMapper}
                  status={value}
                  isViewOnly={false}
                  withTitle
                />
              )}
            />

            <Controller
              name="priority"
              control={control}
              render={({ field: { onChange, value } }) => (
                <PrioritySelector
                  onChange={onChange}
                  priority={value || ProjectPriority.NO_PRIORITY}
                  withTitle
                  trigger={
                    <TaskPropertyButton
                      icon={<PriorityIcon priority={value || ProjectPriority.NO_PRIORITY} />}
                      label={priorities[value || ProjectPriority.NO_PRIORITY]}
                      isActive={!!value}
                    />
                  }
                />
              )}
            />

            <Controller
              name="assigneeId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <AssigneeSelector project={project} selectedMemberId={value} onChange={onChange} />
              )}
            />

            <Controller
              name="dueDate"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CreateTaskDateProperty value={value} onChange={onChange} />
              )}
            />

            {level === 'space' && (
              <Controller
                name="projectId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TaskProjectSelector
                    onChange={(value) => {
                      onChange(value);
                      setValue('stepId', undefined);
                    }}
                    selectedProjectId={value}
                  />
                )}
              />
            )}

            <Controller
              name="stepId"
              control={control}
              render={({ field: { onChange, value } }) => {
                const selectedProject = watchedProjectId
                  ? Project.getOne(watchedProjectId)
                  : undefined;

                return (
                  <Tooltip
                    disabled={!!selectedProject}
                    text="Select project first"
                    position="bottom"
                  >
                    <TaskStepSelector
                      project={selectedProject}
                      onChange={onChange}
                      selectedStepId={value}
                      withIcon
                    />
                  </Tooltip>
                );
              }}
            />

            <Controller
              name="tagsIds"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TagsViewer
                  targetType="task"
                  overflow="wrap"
                  alignment="start"
                  onSelect={onChange}
                  tagsIds={value}
                  fullWidth={false}
                />
              )}
            />
          </div>
        </form>
      </Content>
    </Dialog>
  );
};

const Content = styled.div`
  max-width: 75.2rem;
  flex: 1;
  overflow-y: scroll;

  @media screen and (min-width: ${breakPoint.small}px) {
    width: 75.2rem;
    max-width: 75.2rem;
  }
`;
